import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Avatar,
  InputAdornment,
  Stack,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import AppLayout from "../../components/AppLayout";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";
import API_BASE_URL from "../../config/api";

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [supplierCode, setSupplierCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/suppliers/all/with-amounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch suppliers");

      const data = await response.json();
      setSuppliers(data.suppliers || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      setError("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setEditMode(true);
      setCurrentSupplier(supplier);
      setSupplierCode(supplier.SupplierCode);
    } else {
      setEditMode(false);
      setCurrentSupplier(null);
      setSupplierCode("");
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setCurrentSupplier(null);
    setSupplierCode("");
  };

  const handleSubmit = async () => {
    if (!supplierCode.trim()) {
      toast.error("Supplier code is required");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const url = editMode
        ? `${API_BASE_URL}/api/suppliers/${currentSupplier.SupplierId}`
        : `${API_BASE_URL}/api/suppliers/create`;
      
      const method = editMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ supplierCode: supplierCode.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || `Supplier ${editMode ? "updated" : "created"} successfully`);
        fetchSuppliers();
        handleCloseDialog();
      } else {
        toast.error(data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to save supplier");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (supplier) => {
    setCurrentSupplier(supplier);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentSupplier) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/suppliers/${currentSupplier.SupplierId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Supplier deleted successfully");
        fetchSuppliers();
      } else {
        toast.error(data.message || "Failed to delete supplier");
      }
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.error("Failed to delete supplier");
    } finally {
      setDeleteDialogOpen(false);
      setCurrentSupplier(null);
    }
  };

  // Filter and sort suppliers based on search query and SupplierId
  const filteredSuppliers = suppliers
    .filter((supplier) =>
      supplier.SupplierCode.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.SupplierId - b.SupplierId);

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #151515 0%, #262626 100%)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 700,
            // color: "#fff",
            color: "#C4A574",
            letterSpacing: 2,
            fontSize: { xs: "1.25rem", md: "1.6rem" },
            textDecoration: "underline",
            textUnderlineOffset: 8,
            textDecorationThickness: 2,
          }}
        >
          SUPPLIERS
        </Typography>

        <Box sx={{ maxWidth: 1200, mx: "auto", mb: 4 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Supplier Code..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#C4A574" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "#2f2f2f",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "transparent",
                  color: "#fff",
                  "& fieldset": { borderColor: "rgba(196,165,116,0.06)" },
                  "&:hover fieldset": { borderColor: "rgba(196,165,116,0.16)" },
                },
                "& .MuiInputBase-input::placeholder": { color: "rgba(196,165,116,0.5)" },
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: "#C4A574",
                color: "#000",
                px: 3,
                py: 1.25,
                minWidth: { xs: "100%", sm: "180px" },
                "&:hover": { bgcolor: "#B39565" },
                fontWeight: 700,
              }}
            >
              Add Supplier
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} sx={{justifyContent: 'center'}}>
            {loading ? (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress sx={{ color: "#C4A574" }} />
                </Box>
              </Grid>
            ) : filteredSuppliers.length === 0 ? (
              <Grid item xs={12} sx={{justifyContent: 'center'}}>
                <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
                  <Typography variant="h6" sx={{ color: "#bbb" }}>
                    {searchQuery ? "No suppliers found matching your search" : "No suppliers found"}
                  </Typography>
                  {!searchQuery && (
                    <Typography variant="body2" sx={{ mt: 1, color: "#999" }}>
                      Click 'Add Supplier' to create your first supplier
                    </Typography>
                  )}
                </Box>
              </Grid>
            ) : (
              filteredSuppliers.map((supplier) => {
                const initials = getInitials(supplier.SupplierCode);
                const avatarColor = getAvatarColor(supplier.SupplierCode);

                return (
                  <Grid item key={supplier.SupplierId} xs={12} sm={6} md={4} lg={3}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        transition: "transform 0.25s ease, box-shadow 0.25s ease",
                        "&:hover": { transform: "translateY(-8px)", boxShadow: "0 12px 30px rgba(0,0,0,0.45)" },
                        bgcolor: "#242424",
                        border: supplier.TotalAmountOwed > 0 ? "1px solid rgba(255,152,0,0.18)" : "1px solid rgba(255,255,255,0.03)",
                        position: "relative",
                      }}
                    >
                      {supplier.TotalAmountOwed > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            bgcolor: "#ff9800",
                            color: "#fff",
                            px: 1,
                            py: "2px",
                            borderRadius: 0.75,
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            zIndex: 2,
                          }}
                        >
                          PENDING
                        </Box>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                        <Avatar sx={{ width: 72, height: 72, bgcolor: avatarColor, fontWeight: 700 }}>
                          {initials}
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ color: "#C4A574", fontWeight: 800 }}>
                            {supplier.SupplierCode}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#aaa", mt: 0.5 }}>
                            Supplier ID: {supplier.SupplierId}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ borderColor: "rgba(255,255,255,0.04)" }} />

                      <CardContent sx={{ pt: 1 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: "#1a1a1a",
                            border: "1px solid rgba(255,255,255,0.02)",
                            mb: 2,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "#9e9e9e", display: "block" }}>
                            Total Amount Owed
                          </Typography>
                          <Typography sx={{ fontWeight: 800, color: supplier.TotalAmountOwed > 0 ? "#ff8a65" : "#8bd58b" }}>
                            ₹{parseFloat(supplier.TotalAmountOwed || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </Typography>

                          {(supplier.ProductPurchaseTotal > 0 || supplier.RawMaterialPurchaseTotal > 0) && (
                            <Box sx={{ mt: 0.75 }}>
                              {supplier.ProductPurchaseTotal > 0 && (
                                <Typography variant="caption" sx={{ color: "#9e9e9e", display: "block" }}>
                                  Products: ₹{parseFloat(supplier.ProductPurchaseTotal).toLocaleString("en-IN")}
                                </Typography>
                              )}
                              {supplier.RawMaterialPurchaseTotal > 0 && (
                                <Typography variant="caption" sx={{ color: "#9e9e9e", display: "block" }}>
                                  Materials: ₹{parseFloat(supplier.RawMaterialPurchaseTotal).toLocaleString("en-IN")}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>

                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              startIcon={<PaymentIcon />}
                              onClick={() => navigate(`/admin/suppliers/${supplier.SupplierId}/payments`)}
                              sx={{
                                bgcolor: "#2e7d32",
                                "&:hover": { bgcolor: "#1b5e20" },
                                textTransform: "none",
                                fontWeight: 700,
                              }}
                            >
                              Payments
                            </Button>
                          </Grid>

                          <Grid item xs={6} sm={3}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenDialog(supplier)}
                              sx={{
                                borderColor: "#C4A574",
                                color: "#C4A574",
                                textTransform: "none",
                                fontWeight: 700,
                              }}
                            >
                              Edit
                            </Button>
                          </Grid>

                          <Grid item xs={6} sm={3}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteClick(supplier)}
                              sx={{
                                borderColor: "#d32f2f",
                                color: "#d32f2f",
                                textTransform: "none",
                                fontWeight: 700,
                              }}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Box>

        {/* Bottom decorative fade */}
        <Box sx={{ mt: 6, height: 40, background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.35))", borderRadius: 2 }} />
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#121212", color: "#C4A574" }}>
          {editMode ? "Edit Supplier" : "Add New Supplier"}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#0f0f0f" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Supplier Code"
            type="text"
            fullWidth
            value={supplierCode}
            onChange={(e) => setSupplierCode(e.target.value.toUpperCase())}
            placeholder="e.g., DA, CE, XYZ"
            helperText="Enter a short code for the supplier (2-10 characters)"
            inputProps={{ maxLength: 10 }}
            sx={{
              bgcolor: "transparent",
              "& .MuiInputBase-input": { color: "#fff" },
              "& .MuiFormHelperText-root": { color: "#bfa678" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "rgba(196,165,116,0.08)" },
                "&:hover fieldset": { borderColor: "rgba(196,165,116,0.16)" },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#0f0f0f", px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: "#C4A574", "&:hover": { bgcolor: "#B39565" }, fontWeight: 700 }}
          >
            {submitting ? <CircularProgress size={22} sx={{ color: "#000" }} /> : editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#121212", color: "#C4A574" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#0f0f0f" }}>
          <Typography sx={{ color: "#fff", mt: 1 }}>
            Are you sure you want to delete supplier "{currentSupplier?.SupplierCode}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#0f0f0f", px: 3, py: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: "#C4A574" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            sx={{ 
              bgcolor: "#d32f2f", 
              "&:hover": { bgcolor: "#b71c1c" },
              fontWeight: 700 
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
